import { ExternalLink } from 'lucide-react';
import { apiErrorMessage, type ApiErrorInput } from '~/lib/apiError';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  SkeletonFields,
  LoadError,
} from '~/components/ui';
import { useUrlParams } from '~/hooks/useUrlParams';
import { useGetPreviewUpdatesQuery, usePreviewActionMutation } from '~/modules/query/featureFlags';

import { ProductDrawer } from '@sdk/react/ProductDrawer';
import { ProductUpdatesProvider } from '@sdk/react/ProductUpdatesProvider';
import productUpdatesCss from '@sdk/react/product-updates.css?inline';
import featureRequestsCss from '@sdk/react/feature-requests.css?inline';

import { Select } from '~/components/pages/feature-flags/common';
import type { Reference } from '~/components/pages/feature-flags/common';
import type {
  CreateRequestResult,
  FeatureRequest,
  FeatureRequestDetail,
  FeatureRequestsApi,
  RequestComparison,
  RequestPage,
  VisibleUpdate,
} from '~/components/pages/feature-flags/types';
import { routes } from '~/app/routes';

// The SDK styles itself for a customer's page. Its CSS goes into the frame document only, so it
// never touches the admin; the admin's own CSS never reaches the frame.
const FRAME_DOC = `<!doctype html><html><head><style>body{margin:0;font-family:system-ui,sans-serif}${productUpdatesCss}${featureRequestsCss}</style></head><body></body></html>`;

// Renders children into a blank frame through a portal, so React context and callbacks keep
// working. srcDoc + load is the ready signal: about:blank documents are replaced on load in some
// browsers. The load event fires once, after which the frame body is the portal target.
const PreviewFrame = ({
  open,
  onEscape,
  children,
}: {
  open: boolean;
  onEscape: () => void;
  children: ReactNode;
}) => {
  const [body, setBody] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!body) return;
    // The SDK drawer listens on the global document, which a frame never reaches: forward Escape,
    // and undo the scroll lock it puts on the admin body (the frame is its own scroll root).
    const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && onEscape();
    body.ownerDocument.addEventListener('keydown', onKeyDown);
    if (open) document.body.style.overflow = '';
    return () => body.ownerDocument.removeEventListener('keydown', onKeyDown);
  }, [body, open, onEscape]);

  return (
    <iframe
      title="Product drawer preview"
      srcDoc={FRAME_DOC}
      onLoad={(e) => setBody(e.currentTarget.contentDocument?.body ?? null)}
      className="h-160 w-full rounded-lg border border-gray-200 dark:border-gray-800"
      data-testid="feature-flags-preview-frame"
    >
      {body && createPortal(children, body)}
    </iframe>
  );
};

interface DrawerPreviewProps {
  environment: string;
  product: string;
  updates: VisibleUpdate[];
  values: Record<string, boolean>;
}

// The customers' product drawer, running against the service under the admin's own identity.
const DrawerPreview = ({ environment, product, updates, values }: DrawerPreviewProps) => {
  const [open, setOpen] = useState(false);
  const [previewAction] = usePreviewActionMutation();
  const close = useCallback(() => setOpen(false), []);

  const { api: requestsApi, toggleUpdate } = useMemo(() => {
    const call = <T,>(action: string, payload: object) =>
      previewAction({ environment, product, body: { ...payload, action } }).unwrap() as Promise<T>;
    const requests: FeatureRequestsApi = {
      list: (filters) => call<RequestPage>('list', filters),
      detail: (id) => call<FeatureRequestDetail>('detail', { id }),
      compare: (description) => call<RequestComparison>('compare', { description }),
      create: (input) => call<CreateRequestResult>('create', input),
      vote: (id, voted) => call<FeatureRequest>('vote', { id, voted }),
      enhance: (id, input) => call<FeatureRequestDetail>('enhance', { ...input, id }),
    };
    return {
      api: requests,
      toggleUpdate: async (updateKey: string, enabled: boolean) =>
        (await call<{ value: boolean }>('update-choice', { updateKey, enabled })).value,
    };
  }, [environment, product, previewAction]);

  return (
    <ProductUpdatesProvider updates={values}>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <p className="text-sm text-gray-800 dark:text-white/90">
            Explore product updates, try user toggles and submit feature requests in the same drawer
            your customers use.
          </p>
          <Button
            startIcon={<ExternalLink className="size-4" aria-hidden="true" />}
            onClick={() => setOpen(true)}
            testId="feature-flags-preview-open"
          >
            Open preview
          </Button>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Preview actions use your own identity.
          </p>
        </div>
        <div className="space-y-3 rounded-lg border border-gray-200 p-5 dark:border-gray-800">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium text-gray-800 dark:text-white/90">
              Product updates
            </span>
            <Badge size="sm" color="gray">
              {updates.length} visible
            </Badge>
          </div>
          {updates.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No updates for this audience yet.
            </p>
          ) : (
            updates.slice(0, 3).map((u) => (
              <div
                key={u.id}
                className="rounded-lg border border-gray-200 p-3 dark:border-gray-800"
              >
                <p className="text-sm font-medium text-gray-800 dark:text-white/90">{u.title}</p>
                <p className="line-clamp-2 text-xs text-gray-500 dark:text-gray-400">
                  {u.description}
                </p>
              </div>
            ))
          )}
        </div>
      </div>
      <PreviewFrame open={open} onEscape={close}>
        <ProductDrawer
          open={open}
          onClose={close}
          requestsApi={requestsApi}
          updates={updates}
          onToggleUpdate={toggleUpdate}
          initialTab="updates"
          productLabel={product}
        />
      </PreviewFrame>
    </ProductUpdatesProvider>
  );
};

export const PreviewTab = ({ reference }: { reference: Reference }) => {
  const url = useUrlParams();
  const { environments, products } = reference;
  const environment =
    environments.find((e) => e.key === url.get('environment'))?.key ?? environments[0]?.key ?? '';
  const product = products.find((p) => p.key === url.get('product'))?.key ?? products[0]?.key ?? '';

  const preview = useGetPreviewUpdatesQuery(
    { environment, product },
    { skip: !environment || !product },
  );

  return (
    <Card>
      <CardHeader
        title="Product drawer"
        description="See product updates and feature requests exactly as your customers do."
      />
      <CardBody className="space-y-6">
        {environments.length === 0 || products.length === 0 ? (
          <EmptyState
            title={environments.length === 0 ? 'Add an environment first' : 'Add a product first'}
            description="An environment and a product are needed to preview the product drawer."
            action={
              <Button
                variant="outline"
                href={
                  environments.length === 0
                    ? routes.featureFlagsAdmin('environments')
                    : routes.featureFlagsAdmin('products')
                }
              >
                {environments.length === 0 ? 'Go to environments' : 'Go to products'}
              </Button>
            }
          />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Select
                id="preview-product"
                label="Product"
                value={product}
                options={products.map((p) => ({ value: p.key, label: p.name }))}
                onChange={(v) => url.set({ product: v })}
              />
              <Select
                id="preview-environment"
                label="Environment"
                value={environment}
                options={environments.map((e) => ({ value: e.key, label: e.name }))}
                onChange={(v) => url.set({ environment: v })}
              />
            </div>
            {preview.error && !preview.data ? (
              <LoadError
                what="the preview"
                message={apiErrorMessage(preview.error as ApiErrorInput)}
                onRetry={preview.refetch}
              />
            ) : !preview.data ? (
              <SkeletonFields fields={2} />
            ) : (
              <DrawerPreview
                key={`${environment}:${product}`}
                environment={environment}
                product={product}
                updates={preview.data.updates}
                values={preview.data.values}
              />
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400">
              The environment controls product updates. Feature requests are shared across every
              environment for the selected product.
            </p>
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default PreviewTab;
